<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">{{ pageTitle }}</div>
      <el-button icon="el-icon-refresh" @click="loadProducts">刷新</el-button>
    </div>

    <el-card class="page-card">
      <div class="search-toolbar">
        <el-input
          v-model.trim="filters.name"
          clearable
          :placeholder="`按${entityName}标题查询`"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-input
          v-model.trim="filters.category"
          clearable
          placeholder="按品种查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-input
          v-model.trim="filters.phone"
          clearable
          placeholder="按手机号查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-input
          v-model.trim="filters.supplierName"
          clearable
          placeholder="按发布人名称/昵称查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-input
          v-model.trim="filters.companyName"
          clearable
          placeholder="按公司查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-select v-model="filters.auditStatus" clearable placeholder="审批状态" style="width: 150px;">
          <el-option label="待审批" value="PENDING" />
          <el-option label="已通过" value="APPROVED" />
          <el-option label="已驳回" value="REJECTED" />
        </el-select>
        <el-select v-model="filters.onShelf" clearable placeholder="上架状态" style="width: 150px;">
          <el-option label="上架中" value="true" />
          <el-option label="已下架" value="false" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="loadProducts">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>

      <el-table :key="productType" :data="pagedProducts" v-loading="loading" stripe>
        <el-table-column type="index" :index="paginationIndex" width="56" label="#" />
        <el-table-column label="页面地址" min-width="280">
          <template slot-scope="{ row }">
            <div class="page-address-cell">
              <span class="page-address-text" :title="buildProductPageAddress(row.id)">
                {{ formatProductPageAddress(row.id) }}
              </span>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-document-copy"
                @click="copyProductPageAddress(row.id)"
              >
                复制
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="`${entityName}标题`" min-width="170" />
        <el-table-column label="发布人名称/昵称" min-width="150" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.supplierDisplayName || row.supplierName || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="公司" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.supplierCompanyName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="category" label="品种" width="100" />
        <el-table-column v-if="!isPurchase" label="规格" min-width="130">
          <template slot-scope="{ row }">
            {{ resolveSpecification(row) }}
          </template>
        </el-table-column>
        <el-table-column :label="priceColumnLabel" width="130">
          <template slot-scope="{ row }">
            <span v-if="formatPrice(row.price)">
              <span>{{ formatPrice(row.price) }}</span><span class="price-unit">/吨</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="quantityColumnLabel" width="120">
          <template slot-scope="{ row }">
            {{ formatQuantity(row.stock) }}
          </template>
        </el-table-column>
        <el-table-column v-if="isPurchase" prop="description" label="正文" min-width="180" show-overflow-tooltip />
        <el-table-column v-if="!isPurchase" prop="productionDate" label="生产日期" width="120" />
        <el-table-column label="审批状态" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="auditTagType(row)">
              {{ auditLabel(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上架状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.onShelf ? 'success' : 'info'">
              {{ row.onShelf ? '上架中' : '已下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="发布时间" width="170" />
        <el-table-column label="操作" width="350" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="showDetail(row)">查看</el-button>

            <el-button
              v-if="isPending(row) || isRejected(row)"
              size="mini"
              type="success"
              @click="approveProduct(row)"
            >
              审核通过
            </el-button>

            <el-button
              v-if="isPending(row) || isApproved(row)"
              size="mini"
              type="danger"
              @click="rejectProduct(row)"
            >
              驳回
            </el-button>

            <el-button
              v-if="isApproved(row)"
              size="mini"
              :type="row.onShelf ? 'warning' : 'primary'"
              @click="toggleShelf(row)"
            >
              {{ row.onShelf ? '下架' : '上架' }}
            </el-button>

            <el-button
              size="mini"
              type="danger"
              :loading="deletingId === row.id"
              @click="deleteProduct(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="list-pagination">
        <el-pagination
          background
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="pageSizes"
          :total="products.length"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handlePageSizeChange"
          @current-change="handleCurrentPageChange"
        />
      </div>
    </el-card>

    <el-dialog :title="`${entityName}详情`" :visible.sync="detailVisible" width="780px">
      <div v-if="current">
        <el-descriptions :column="2" border>
          <el-descriptions-item :label="`${entityName}ID`">{{ formatDisplayProductId(current.id) }}</el-descriptions-item>
          <el-descriptions-item :label="`${entityName}标题`">{{ current.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="品种">{{ current.category || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchase" label="规格">{{ resolveSpecification(current) }}</el-descriptions-item>
          <el-descriptions-item :label="priceColumnLabel">
            <span v-if="formatPrice(current.price)">
              <span>{{ formatPrice(current.price) }}</span><span class="price-unit">/吨</span>
            </span>
          </el-descriptions-item>
          <el-descriptions-item :label="quantityColumnLabel">{{ formatQuantity(current.stock) }}</el-descriptions-item>
          <el-descriptions-item v-if="isPurchase" label="正文" :span="2">{{ current.description || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchase" label="货物所在地">{{ current.region || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchase" label="产地">{{ current.productionPlace || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchase" label="生产日期">{{ current.productionDate || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发布人名称/昵称">{{ current.supplierDisplayName || current.supplierName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="公司">{{ current.supplierCompanyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审批状态">{{ auditLabel(current) }}</el-descriptions-item>
          <el-descriptions-item label="驳回/审批说明" :span="2">{{ current.auditComment || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ current.createTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="展示图片" :span="2">
            <div v-if="resolveDetailImages(current).length" class="detail-images">
              <el-image
                v-for="(image, index) in resolveDetailImages(current)"
                :key="`${index}-${image}`"
                :src="image"
                :preview-src-list="resolveDetailImages(current)"
                fit="cover"
                class="detail-image-item"
              />
            </div>
            <span v-else>-</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchProducts } from '../../api/admin'
import productManagementMixin from '../../utils/product-management'

export default {
  name: 'ProductListView',
  mixins: [productManagementMixin],
  data() {
    return {
      loading: false,
      products: [],
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      filters: {
        name: '',
        category: '',
        phone: '',
        supplierName: '',
        companyName: '',
        auditStatus: '',
        onShelf: ''
      }
    }
  },
  created() {
    this.loadProducts()
  },
  computed: {
    productType() {
      const type = String((this.$route.meta && this.$route.meta.productType) || 'SALE').trim().toUpperCase()
      return type === 'PURCHASE' ? 'PURCHASE' : 'SALE'
    },
    isPurchase() {
      return this.productType === 'PURCHASE'
    },
    entityName() {
      return this.isPurchase ? '求购' : '销售'
    },
    pageTitle() {
      return `${this.entityName}管理`
    },
    priceColumnLabel() {
      return this.isPurchase ? '期望价格（元/吨）' : '价格（元/吨）'
    },
    quantityColumnLabel() {
      return this.isPurchase ? '求购数量（吨）' : '库存（吨）'
    },
    pagedProducts() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.products.slice(start, start + this.pageSize)
    }
  },
  watch: {
    productType() {
      this.current = null
      this.detailVisible = false
      this.resetFilters()
    }
  },
  methods: {
    paginationIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    handleCurrentPageChange(page) {
      this.currentPage = page
    },
    adjustCurrentPage() {
      const maxPage = Math.max(1, Math.ceil(this.products.length / this.pageSize))
      this.currentPage = Math.min(this.currentPage, maxPage)
    },
    async loadProducts() {
      this.currentPage = 1
      this.loading = true
      try {
        const params = {}
        const name = String(this.filters.name || '').trim()
        const category = String(this.filters.category || '').trim()
        const phone = String(this.filters.phone || '').trim()
        const supplierName = String(this.filters.supplierName || '').trim()
        const companyName = String(this.filters.companyName || '').trim()
        const auditStatus = String(this.filters.auditStatus || '').trim()
        const onShelf = String(this.filters.onShelf || '').trim()
        if (name) params.name = name
        if (category) params.category = category
        if (phone) params.phone = phone
        if (supplierName) params.supplierName = supplierName
        if (companyName) params.companyName = companyName
        if (auditStatus) params.auditStatus = auditStatus
        if (onShelf) params.onShelf = onShelf
        params.type = this.productType
        const products = await fetchProducts(params)
        this.products = Array.isArray(products) ? products : []
      } catch (error) {
        this.$message.error(error.message || `${this.entityName}列表获取失败`)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = {
        name: '',
        category: '',
        phone: '',
        supplierName: '',
        companyName: '',
        auditStatus: '',
        onShelf: ''
      }
      this.loadProducts()
    },
    onProductDeleted() {
      this.adjustCurrentPage()
    },
    buildProductPageAddress(value) {
      const raw = String(value || '').trim()
      if (!raw) return '-'
      return `/pages/product-detail/product-detail?id=${encodeURIComponent(raw)}`
    },
    formatProductPageAddress(value) {
      const raw = String(value || '').trim()
      return raw ? `...${raw}` : '-'
    },
    async copyProductPageAddress(value) {
      const address = this.buildProductPageAddress(value)
      if (!address || address === '-') {
        this.$message.warning('页面地址为空')
        return
      }
      try {
        await this.writeClipboardText(address)
        this.$message.success('页面地址已复制')
      } catch (error) {
        this.$message.error('复制失败，请手动复制')
      }
    },
    writeClipboardText(text) {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        return navigator.clipboard.writeText(text)
      }
      return new Promise((resolve, reject) => {
        if (typeof document === 'undefined' || !document.body) {
          reject(new Error('clipboard unavailable'))
          return
        }
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', 'readonly')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '0'
        document.body.appendChild(textarea)
        textarea.select()
        try {
          const copied = document.execCommand('copy')
          copied ? resolve() : reject(new Error('copy command failed'))
        } catch (error) {
          reject(error)
        } finally {
          document.body.removeChild(textarea)
        }
      })
    }
  }
}
</script>

<style scoped>
.search-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.list-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.text-ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.page-address-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.page-address-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price-unit {
  margin-left: 1px;
  font-size: 0.92em;
  color: #909399;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.detail-image-item {
  width: 100%;
  height: 110px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}
</style>
